import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FlaskConical, Info, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { introductionPreviewSchema, introductionStatusSchema, PERMISSIONS } from '@erp/shared';
import { usePermissions, useSession } from '@/features/auth';
import { apiFetch } from '@/lib/api-client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

function useIntroductionStatus() {
  const session = useSession();
  return useQuery({
    queryKey: ['introduction-status', session.data?.tenantId],
    queryFn: () => apiFetch('/api/v1/introduction/status', introductionStatusSchema),
    enabled: session.data !== null && session.data !== undefined,
    staleTime: 0,
    refetchInterval: 5_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: 'always',
    retry: false,
  });
}

export function IntroductionBanner(): JSX.Element {
  const { t } = useTranslation();
  const status = useIntroductionStatus();
  const { can } = usePermissions();
  const ref = useRef<HTMLDivElement>(null);
  const detailsTrigger = useRef<HTMLButtonElement>(null);
  const mode = status.isError ? undefined : status.data?.mode;
  const [detailsOpen, setDetailsOpen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (node === null) return;
    const resize = (): void =>
      document.documentElement.style.setProperty(
        '--operating-banner-height',
        `${node.getBoundingClientRect().height}px`,
      );
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(node);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--operating-banner-height');
    };
  }, []);
  const label = status.isError
    ? t('introduction.unknown')
    : status.data === undefined
      ? t('introduction.loading')
      : mode === 'INTRODUCTION'
        ? t('introduction.banner')
        : t(`introduction.${mode}`);
  const environment = t(
    `introduction.${status.data?.environment === 'unknown' || status.data === undefined ? 'environmentUnknown' : status.data.environment}`,
  );
  const compactLabel = status.isError
    ? t('header.statusUnknown')
    : status.data === undefined
      ? t('header.statusLoading')
      : mode === 'INTRODUCTION'
        ? t('header.introductionActive')
        : mode === 'UNCLASSIFIED'
          ? t('header.protected')
          : t(`introduction.${mode}`);
  const Icon = mode === 'INTRODUCTION' ? FlaskConical : mode === 'LIVE' ? ShieldCheck : ShieldAlert;
  return (
    <>
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        data-testid="introduction-banner"
        className="fixed inset-x-0 top-0 z-[100] flex min-h-11 items-center gap-2 border-b border-border bg-secondary ps-[max(.75rem,env(safe-area-inset-left))] pe-[max(.75rem,env(safe-area-inset-right))] pt-[env(safe-area-inset-top)] text-secondary-foreground"
      >
        <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 py-1 text-xs leading-4 sm:flex sm:items-center sm:gap-2">
          <span className="block">{environment}</span>
          <span className="hidden sm:inline" aria-hidden="true">
            ·
          </span>
          <span className="block font-semibold">{compactLabel}</span>
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11 shrink-0 md:h-11 md:w-11"
          ref={detailsTrigger}
          aria-label={t('introduction.title')}
          onClick={() => setDetailsOpen(true)}
        >
          <Info aria-hidden="true" className="h-5 w-5" />
        </Button>
      </div>
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent
          className="[&>button]:h-11 [&>button]:w-11"
          closeLabel={t('common.close')}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            detailsTrigger.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('introduction.title')}</DialogTitle>
            <DialogDescription>
              {environment} · {label}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm">{t('introduction.safety')}</p>
          {status.isError ? (
            <Button variant="outline" onClick={() => void status.refetch()}>
              {t('introduction.retry')}
            </Button>
          ) : null}
          {can(PERMISSIONS.INTRODUCTION_MANAGE) ? (
            <Button asChild variant="outline">
              <Link to="/settings/introduction" onClick={() => setDetailsOpen(false)}>
                {t('introduction.manage')}
              </Link>
            </Button>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function IntroductionRoute(): JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  const { can } = usePermissions();
  const allowed = can(PERMISSIONS.INTRODUCTION_MANAGE);
  const preview = useQuery({
    queryKey: ['introduction-preview', session.data?.tenantId],
    queryFn: () => apiFetch('/api/v1/introduction/preview', introductionPreviewSchema),
    enabled: allowed,
    retry: false,
  });
  if (!allowed) return <p role="alert">{t('introduction.noAccess')}</p>;
  return (
    <section className="space-y-5" aria-labelledby="introduction-title">
      <h1 id="introduction-title" className="text-xl font-semibold">
        {t('introduction.title')}
      </h1>
      <p className="max-w-3xl text-sm">{t('introduction.safety')}</p>
      <Button
        type="button"
        variant="outline"
        disabled={preview.isFetching}
        onClick={() => void preview.refetch()}
      >
        {t('introduction.retry')}
      </Button>
      {preview.isPending ? (
        <div className="h-32 animate-pulse rounded bg-muted" aria-busy="true">
          {t('introduction.loading')}
        </div>
      ) : null}
      {preview.isError ? <p role="alert">{t('introduction.failed')}</p> : null}
      {preview.data === undefined ? null : (
        <>
          <p className="break-words font-medium">
            {preview.data.status.tenantName} · {t(`introduction.${preview.data.status.mode}`)}
          </p>
          {preview.data.status.phaseId === null ? null : (
            <p className="break-all">
              {t('introduction.phase')}: {preview.data.status.phaseId}
            </p>
          )}
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              ['preserved', preview.data.areas.reduce((sum, area) => sum + area.preservedCount, 0)],
              ['tests', preview.data.areas.reduce((sum, area) => sum + area.testCount, 0)],
              ['files', preview.data.files],
            ].map(([key, count]) => (
              <div key={key} className="rounded border border-border p-4">
                <dt>{t(`introduction.${key}`)}</dt>
                <dd className="text-2xl font-semibold">{count}</dd>
              </div>
            ))}
          </dl>
          {preview.data.areas.every((area) => area.testCount === 0) ? (
            <p>{t('introduction.empty')}</p>
          ) : null}
          <h2 className="font-semibold">{t('introduction.blockers')}</h2>
          <ul className="list-disc space-y-2 ps-5">
            {preview.data.blockers.map((blocker, index) => (
              <li key={`${blocker.code}-${index}`}>
                {t(`introduction.${blocker.code}`)} ({blocker.count})
              </li>
            ))}
          </ul>
          <p className="text-sm">{t('introduction.retained')}</p>
          <p className="text-sm text-muted-foreground">{t('introduction.backups')}</p>
        </>
      )}
    </section>
  );
}
